import { lazy, Suspense, useState } from 'react'

import ChatIcon from '@heroicons/react/outline/esm/ChatIcon.js'
import DotsHorizontalIcon from '@heroicons/react/outline/esm/DotsHorizontalIcon.js'
import PencilIcon from '@heroicons/react/outline/esm/PencilIcon.js'
import { Link as RouterLink } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Member } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { generateNewChatPath } from '../../utils/messaging.utils.js'
import { MemberActionItems } from './MemberActionItems.js'

const ProfileSettingsModal = lazy(() =>
  import('@tribeplatform/react-components/ProfileSettingsModal').then(m => ({
    default: m.ProfileSettingsModal,
  })),
)

export const MemberActionButtons = ({ member }: { member: Member }) => {
  const { $t } = useI18n()

  const [editModal, setEditModal] = useState(false)
  const {
    data: { member: authMember },
  } = useAuthToken()
  const [canUpdateMember] = hasScopesPermission(member, ['updateMember'])

  return (
    <>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal
          aria-label={$t({
            defaultMessage: 'Options',
            id: 'Generics.Options',
          })}
        >
          <IconButton
            as="span"
            variant="secondaryNeutral"
            icon={<DotsHorizontalIcon />}
          />
        </Dropdown.ButtonMinimal>
        <MemberActionItems member={member} context="member" />
      </Dropdown>

      {canUpdateMember ? (
        <Button
          onClick={() => setEditModal(true)}
          variant="secondaryNeutral"
          className="flex-shrink-0"
          leadingIcon={<PencilIcon />}
        >
          <T defaultMessage="Edit profile" id="Generics.EditProfile" />
        </Button>
      ) : null}
      {member?.authMemberProps?.canSendPrivateMessages &&
      member?.id !== authMember?.id ? (
        <Button
          variant="primary"
          as={RouterLink}
          to={generateNewChatPath(member)}
          className="flex-shrink-0"
          leadingIcon={<ChatIcon />}
        >
          <T defaultMessage="Message" id="Generics.Message.Verb" />
        </Button>
      ) : null}

      {editModal && (
        <Suspense fallback={<></>}>
          <ProfileSettingsModal
            open
            onClose={() => {
              setEditModal(false)
            }}
            member={member}
          />
        </Suspense>
      )}
    </>
  )
}
